import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Star, Warning, CheckCircle, Envelope, Clock } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AdminShell } from "@/components/Layout";

export default function AdminReviews() {
  const [data, setData] = useState(null);
  const [urls, setUrls] = useState({ google: "", yelp: "", facebook: "" });
  const [savingUrls, setSavingUrls] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/reviews");
      setData(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    }
    try {
      const t = await api.get("/tenant");
      setUrls(t.data?.review_urls || { google: "", yelp: "", facebook: "" });
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const saveUrls = async () => {
    setSavingUrls(true);
    try {
      await api.put("/tenant/review-urls", urls);
      toast.success("Review URLs saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSavingUrls(false); }
  };

  return (
    <AdminShell>
      <div className="max-w-5xl mx-auto px-4 py-6" data-testid="reviews-page">
        <div className="flex items-center gap-3">
          <Star weight="fill" size={30} className="text-amber-500"/>
          <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight">Reviews</h1>
        </div>
        <p className="mt-1 text-sm text-slate-600 max-w-2xl">
          Tenant reviews collected via the "Send Review Request" button on any completed job. ≥4 stars nudged to Google/Yelp; ≤3 stars stay private (emailed to admins).
        </p>

        {data && (
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-2 text-center" data-testid="reviews-stats">
            <Stat label="Sent" value={data.total}/>
            <Stat label="Submitted" value={data.submitted}/>
            <Stat label="Public (4-5★)" value={data.reviews.filter(r => r.is_public === true).length}/>
            <Stat label="Avg Rating" value={data.average ? `${data.average}★` : "—"}/>
          </div>
        )}

        <section className="mt-6 rugged-card p-4" data-testid="reviews-urls-panel">
          <h2 className="font-display font-black uppercase tracking-widest text-sm">Public Review URLs</h2>
          <p className="text-xs text-slate-600 mb-3">Where 4-5 star tenants get pushed. Google is what matters most.</p>
          {["google", "yelp", "facebook"].map((k) => (
            <div key={k} className="mb-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">{k}</label>
              <input value={urls[k] || ""} onChange={(e) => setUrls({ ...urls, [k]: e.target.value })}
                data-testid={`reviews-url-${k}`}
                placeholder={k === "google" ? "https://g.page/r/…/review" : `https://${k}.com/…`}
                className="w-full h-10 px-3 border-2 border-[#0f172a] text-sm font-mono"/>
            </div>
          ))}
          <button onClick={saveUrls} disabled={savingUrls} data-testid="reviews-save-urls"
            className="mt-2 h-10 px-5 bg-[#0f172a] text-white text-[11px] font-black uppercase tracking-widest hover:opacity-90 disabled:opacity-40">
            {savingUrls ? "Saving…" : "Save URLs"}
          </button>
        </section>

        <div className="mt-6 space-y-2" data-testid="reviews-list">
          {!data && <div className="text-center text-slate-500 font-bold uppercase text-xs">Loading…</div>}
          {data?.reviews?.length === 0 && (
            <div className="rugged-card p-10 text-center" data-testid="reviews-empty">
              <Star size={48} className="mx-auto text-slate-300"/>
              <div className="mt-3 font-display font-black text-lg uppercase text-slate-500">No reviews yet</div>
              <div className="mt-1 text-sm text-slate-500">Hit "Send Review Request" on a completed Job Detail.</div>
            </div>
          )}
          {data?.reviews?.map((r) => {
            const submitted = r.stars !== null && r.stars !== undefined;
            const isBad = submitted && r.stars <= 3;
            return (
              <div key={r.id} data-testid={`review-row-${r.id}`} className="rugged-card p-4 flex flex-wrap items-start gap-3">
                <div className="flex items-center gap-1">
                  {submitted ? [1,2,3,4,5].map(n => (
                    <Star key={n} weight={n <= r.stars ? "fill" : "regular"} size={16}
                      className={n <= r.stars ? "text-amber-400" : "text-slate-300"}/>
                  )) : <Clock size={16} className="text-slate-400"/>}
                </div>
                <div className="flex-1 min-w-[220px]">
                  <div className="font-black text-sm">{r.property_address || "—"}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {r.tenant_name || "(no name)"} · <span className="font-mono">{r.to_email}</span>
                  </div>
                  {r.comment && (
                    <div className={`mt-2 text-xs italic p-2 border-l-4 ${isBad ? "border-red-500 bg-red-50" : "border-emerald-500 bg-emerald-50"}`}>
                      "{r.comment}"
                    </div>
                  )}
                </div>
                <div className="text-right text-[10px] font-black uppercase tracking-widest">
                  {isBad ? (
                    <span className="text-red-700 inline-flex items-center gap-1"><Warning size={11} weight="fill"/> Private</span>
                  ) : r.is_public ? (
                    <span className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle size={11} weight="fill"/> Public</span>
                  ) : (
                    <span className="text-slate-500 inline-flex items-center gap-1"><Envelope size={11}/> Sent, waiting</span>
                  )}
                  <div className="text-slate-500 mt-1 font-normal">{(r.submitted_at || r.sent_at || "").slice(0,10)}</div>
                  {r.job_id && <Link to={`/admin/job/${r.job_id}`} className="text-[10px] underline block mt-1">Job →</Link>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </AdminShell>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rugged-card p-3">
      <div className="text-2xl font-black font-display">{value}</div>
      <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mt-1">{label}</div>
    </div>
  );
}
